// tslint:disable: no-string-literal
import { Injectable } from '@angular/core';
import { JSONObject, JSONValue, ProductType } from '../../class/workflow.type';
import { MergeConfigHelper } from '../../helper/merge-config/merge-config.helper';
import { mergeJson } from '../../helper/merge-json/merge-json';

import { Config, ProductsConfig } from '../../class/config.type';
import { widgetConfigBundleApi, WidgetContextDataShared } from '../../widget/widget-context-data.shared';

/**
 * Provides global configuration data to construct the application flow:
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  /**
   * indicate the current product
   */
  public currentProducts: ProductType[] = [];

  /**
   * Generic config container
   */
  // eslint-disable-next-line @typescript-eslint/dot-notation
  public config: Config = window['_config'] || {};

  /**
   * Generic product config separated by product
   */
  public productConfig: ProductsConfig = {};

  /**
   * An instance to merge config helper
   */
  private readonly mergeConfigHelper: MergeConfigHelper = new MergeConfigHelper();

  /**
   * Get fragment from JSON hierarchy if path is found
   * @param obj data structure
   * @param path slash-separated hierarchy path
   * @param fullPathOnly return value only if the full path is found
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public static jsonResolvePath(obj: JSONObject, path: string, fullPathOnly?: boolean): JSONObject {
    let data = obj;
    const levels = path
      .split('/')
      .filter((v) => (v || '').trim())
      .map((v) => v.trim());
    while (levels.length && typeof data === 'object') {
      data = data[levels.shift()] as JSONObject;
    }
    return data;
  }

  constructor() {
    this.buildConfigs();
  }

  public addExternalConfig(externalConfig: ProductsConfig): void {
    this.mergeConfigHelper.mergeExternalConfig(this.config, this.productConfig, externalConfig);
    this.mergeConfigHelper.combineProductConfigsFiles(this.config);
  }

  public configByProducts(products: ProductType[]): Config {
    const config = {};

    if (!products) {
      return config;
    }

    mergeJson(config, ...(products.map((p) => this.productConfig[p]) as JSONValue[]));
    return config;
  }

  public get tenantId(): string {
    const tenantId = this.getTenantIdFromContext();
    if (tenantId) {
      return tenantId;
    }
    return this.config['storage-info'].tenantId;
  }
  public get businessUnitId(): string {
    return this.getContextPropertyFromCustomConfig('businessUnitId');
  }
  public get userId(): string {
    return this.getContextPropertyFromCustomConfig('userId');
  }

  /**
   * Getter for API url base
   */
  public get apiRoot(): string {
    const rootUrl = WidgetContextDataShared.widgetContext?.config?.bundleRoot;
    return rootUrl;
  }

  /**
   * Read a config value by path
   * @param path slash-separated path
   * @param primitiveValueOnly only return non-object (incl. non-array) values
   * @param jsonObject targeted object, default to global config if not provided.
   */
  public read(path: string, primitiveValueOnly?: boolean, jsonObject?: JSONObject): JSONValue {
    const value = ConfigService.jsonResolvePath(jsonObject || (this.config as JSONObject), path, true);
    if (!primitiveValueOnly || typeof value !== 'object') {
      return value;
    }
  }

  /**
   * Getter for Root or Config API url base
   * TODO: sanitize environment dependency
   */
  private getUrlFromStorageInfo(): string {
    const bundleApi = widgetConfigBundleApi();

    if (bundleApi) {
      return bundleApi;
    }

    if (!this.config['storage-info']) {
      console.warn('storage-info config not found. Make sure storage-info.json file exists.');
      return;
    }

    return this.config['storage-info'].root;
  }

  private getTenantIdFromContext(): string {
    const context = WidgetContextDataShared.widgetContext;
    return context && context.tenantId;
  }

  /**
   * Read property values in context custom config
   * @param key property key in widget context custom config
   * @returns key value
   */
  private getContextPropertyFromCustomConfig(key: string): string {
    const context = WidgetContextDataShared.widgetContext.config && WidgetContextDataShared.widgetContext.config.customConfig;
    return context && context[key];
  }
  /**
   * This function is responsible to build config per products
   */
  private buildConfigs(): void {
    this.productConfig = this.mergeConfigHelper.buildConfigPerProduct(this.config);
    this.mergeConfigHelper.combineProductConfigsFiles(this.config);
  }
}
